// Edit layer style rule: Geometry

import { MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { ILayer, IVectorLayerAttribute } from '../../../Layers/LayerInterfaces';
import { theme_errorRed, theme_limeGreen, theme_orange, theme_textColorBlended, theme_textColorMain } from '../../../Theme';
import { CustomSelect } from '../EditLayerStyle';
import { ChangeEvent, ReactNode } from 'react';
import { IColorSchemeType } from '../../../Aois/AoiGroupAndPortfolioMap/PortfolioMapInterfaces';
import Debug from '../../../Debug';
import { CallServer } from '../../../CallServer';
import { ToastNotification } from '../../../ToastNotifications';
import { GetVectorLayerAttributeByID } from '../../../AttributeOps';
import { EditLayerStyle_ColorScheme_SingleColor, GetDefaultLayerStyle_SingleColor, ValidateLayerStyleRuleGeom_SingleColor } from './EditLayerStyle_ColorScheme_SingleColor';
import { EditLayerStyle_ColorScheme_UniqueValues, GetDefaultLayerStyle_UniqueValues, LayerStyle_RegenerateUniqueValues, ValidateLayerStyleRuleGeom_UniqueValues } from './EditLayerStyle_ColorScheme_UniqueValues';
import { EditLayerStyle_ColorScheme_Classified, GetDefaultLayerStyle_Classified, LAYER_STYLE_CLASSIFIED_DEFAULT_CLASS_COUNT, LAYER_STYLE_CLASSIFIED_DEFAULT_PRESET_ID, LayerStyle_CreateClassifiedRanges_GradientPreset, ValidateLayerStyleRuleGeom_Classified } from './EditLayerStyle_ColorScheme_Classified';
import { EditLayerStyle_ColorScheme_Gradient, GetDefaultLayerStyle_Gradient, LAYER_STYLE_GRADIENT_DEFAULT_STOP_COUNT, LAYER_STYLE_GRADIENT_DEFAULT_PRESET_ID, LayerStyle_CreateGradientStops_GradientPreset, ValidateLayerStyleRuleGeom_Gradient } from './EditLayerStyle_ColorScheme_Gradient';
import { ILayerStyleRule, ILayerStyleRule_Geometry, ILayerStyleType_Classified, ILayerStyleType_Gradient, ILayerStyleType_SingleColor, ILayerStyleType_UniqueValues, LAYER_STYLE_LINE_TYPES, LAYER_STYLE_POINT_TYPES, LAYER_STYLE_POLY_FILL_PATTERNS, LAYER_STYLE_RULE_COLOR_SCHEMES, TLayerStyleAttribType, TLayerStyleLineType, TLayerStylePointType, TLayerStylePolyFillPattern, TLayerStyleShapeType } from './LayerStyleInterfaces';
import { MuiColorInputColors } from 'mui-color-input';
import TypographyWithAutoTooltip from '../../../Components/TypograpyWithAutoTooltip';
import { GetGradientPreset } from '../../../Aois/AoiGroupAndPortfolioMap/ColorSchemeEditor/ColorGradientPresets';
import { EditLayerStyleRule_Setting_Number } from './EditLayerStyleRule_Setting_Number';
import { EditLayerStyleRule_Setting_Color } from './EditLayerStyleRule_Setting_Color';
import { EditLayerStyleRule_Setting_Dropdown, RenderDefaultValue_Dropdown } from './EditLayerStyleRule_Setting_Dropdown';


export const POINT_TYPE_DEFAULT = 'circle';
export const POINT_SIZE_DEFAULT = 10;
const POINT_MIN_SIZE = 0;
const POINT_MAX_SIZE = 30;
export const POINT_OPACITY_DEFAULT = 1.0;
const POINT_HALO_MIN_SIZE = 0;
const POINT_HALO_MAX_SIZE = 40;
export const POINT_HALO_SIZE_DEFAULT = 12;
export const POINT_HALO_COLOR_DEFAULT = '#000000'
export const POINT_HALO_OPACITY_DEFAULT = 0.5;

const LINE_THICKNESS_MIN = 0;
const LINE_THICKNESS_MAX = 20;
export const LINE_THICKNESS_DEFAULT = 4;
export const LINE_OPACITY_DEFAULT = 1.0;
export const LINE_STYLE_DEFAULT: TLayerStyleLineType = 'solid';
const LINE_HALO_MIN_THICKNESS = 0;
const LINE_HALO_MAX_THICKNESS = 25;
export const LINE_HALO_THICKNESS_DEFAULT = 5.5;
export const LINE_HALO_COLOR_DEFAULT = '#000000';
export const LINE_HALO_OPACITY_DEFAULT = 0.5;

export const POLY_FILL_OPACITY_DEFAULT = 0.3;
export const POLY_FILL_PATTERN_DEFAULT: TLayerStylePolyFillPattern = 'No Pattern';
export const POLY_FILL_PATTERN_COLOR_DEFAULT = '#A0A0A0';
const POLY_FILL_PATTERN_SPACING_MIN = 1;
const POLY_FILL_PATTERN_SPACING_MAX = 200;
export const POLY_FILL_PATTERN_SPACING_DEFAULT = 20;
const POLY_FILL_PATTERN_STROKE_WIDTH_MIN = 1;
const POLY_FILL_PATTERN_STROKE_WIDTH_MAX = 20;
export const POLY_FILL_PATTERN_STROKE_WIDTH_DEFAULT = 6;
export const POLY_FILL_PATTERN_OPACITY_DEFAULT = 0.5;

//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyleRuleGeomProps
{
  layer?: ILayer;
  layer_shape_type: TLayerStyleShapeType;
  rule: ILayerStyleRule;   // input
  defaultRule: ILayerStyleRule;   // input
  is_default_rule: boolean;
  OnRuleGeomUpdated: any;      // output
  OnExpandRule: any;
}

//-------------------------------------------------------------------------------
// Edit layer style rule: Geometry
//-------------------------------------------------------------------------------
export function EditLayerStyleRuleGeom(props: EditLayerStyleRuleGeomProps) 
{










  //-------------------------------------------------------------------------------
  // The color scheme type combo box was changed.
  //-------------------------------------------------------------------------------
  const OnColorSchemeTypeChanged = (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.rule.geometry) return;

    const newColorScheme: IColorSchemeType = event.target.value as IColorSchemeType;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      color_scheme: newColorScheme
    }

    // Initialize the appropriate json key depending on the selected color scheme type

    if(newColorScheme === 'single color') 
      updatedRuleGeom.single_color = GetDefaultLayerStyle_SingleColor();
    else if(newColorScheme === 'unique values') 
      updatedRuleGeom.unique_values = GetDefaultLayerStyle_UniqueValues();
    else if(newColorScheme === 'classified') 
      updatedRuleGeom.classified = GetDefaultLayerStyle_Classified();
    else if(newColorScheme === 'gradient')
      updatedRuleGeom.gradient = GetDefaultLayerStyle_Gradient();
        
    // Delete json keys used for the old type

    if(newColorScheme !== 'single color') delete updatedRuleGeom.single_color;
    if(newColorScheme !== 'unique values') delete updatedRuleGeom.unique_values;
    if(newColorScheme !== 'classified') delete updatedRuleGeom.classified;
    if(newColorScheme !== 'gradient') delete updatedRuleGeom.gradient;

    // Reset the selected attribute
    delete updatedRuleGeom.attribute_id;
    delete updatedRuleGeom.attribute_type;

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The color scheme attribute combo box was changed.
  //-------------------------------------------------------------------------------
  const OnAttributeChanged = async (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.rule.geometry || !props.rule.geometry.color_scheme || !props.layer) return;

    const newAttributeID: number = event.target.value as number;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      attribute_id: newAttributeID,
      attribute_type: GetLayerStyleAttribType(props.layer, newAttributeID)
    }

    // For Unique Values mode, we initialize the list based on each unique value of this attribute
    
    if(updatedRuleGeom.color_scheme === 'unique values')
      updatedRuleGeom.unique_values = await LayerStyle_RegenerateUniqueValues(newAttributeID, true);

    // For classified, we default to a 4-class Yellow-Orange-Red preset

    if(props.rule.geometry.color_scheme === 'classified')
    {
      const gradient_preset: ILayerStyleType_Gradient | undefined = GetGradientPreset(LAYER_STYLE_CLASSIFIED_DEFAULT_PRESET_ID);
      updatedRuleGeom.classified = await LayerStyle_CreateClassifiedRanges_GradientPreset(
                                            updatedRuleGeom, 
                                            LAYER_STYLE_CLASSIFIED_DEFAULT_CLASS_COUNT, 
                                            gradient_preset);
    }

    // For gradient mode, we default to a Yellow-Orange-Red preset

    if(props.rule.geometry.color_scheme === 'gradient')
    {
      const gradient_preset: ILayerStyleType_Gradient | undefined = GetGradientPreset(LAYER_STYLE_GRADIENT_DEFAULT_PRESET_ID);
      updatedRuleGeom.gradient = await LayerStyle_CreateGradientStops_GradientPreset(
                                            updatedRuleGeom, 
                                            LAYER_STYLE_GRADIENT_DEFAULT_STOP_COUNT,
                                            gradient_preset);
      }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);    
  }

  //-------------------------------------------------------------------------------
  // The point type combo box was changed.
  //-------------------------------------------------------------------------------
  const OnPointTypeChanged = (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      point_type: event.target.value as TLayerStylePointType
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The point size has changed.
  //-------------------------------------------------------------------------------
  function OnPointSizeChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      point_size: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The point opacity has changed.
  //-------------------------------------------------------------------------------
  function OnPointOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      point_opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The point halo size has changed.
  //-------------------------------------------------------------------------------
  function OnPointHaloSizeChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      point_halo_size: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The point halo opacity has changed.
  //-------------------------------------------------------------------------------
  function OnPointHaloOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      point_halo_opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The "Point Halo Color" color has changed.
  //-------------------------------------------------------------------------------
  function OnPointHaloColorChanged(value: string, colors: MuiColorInputColors)
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      point_halo_color: value
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The line style combo box was changed.
  //-------------------------------------------------------------------------------
  const OnLineStyleChanged = (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      line_style: event.target.value as TLayerStyleLineType
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The line thickness has changed.
  //-------------------------------------------------------------------------------
  function OnLineThicknessChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      line_thickness: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The line opacity has changed.
  //-------------------------------------------------------------------------------
  function OnLineOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      line_opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The line halo thickness has changed.
  //-------------------------------------------------------------------------------
  function OnLineHaloThicknessChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      line_halo_thickness: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The line halo opacity has changed.
  //-------------------------------------------------------------------------------
  function OnLineHaloOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      line_halo_opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The "Line Halo Color" color has changed.
  //-------------------------------------------------------------------------------
  function OnLineHaloColorChanged(value: string, colors: MuiColorInputColors)
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      line_halo_color: value
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The poly fill opacity has changed.
  //-------------------------------------------------------------------------------
  function OnPolyFillOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      poly_fill_opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }
  
  //-------------------------------------------------------------------------------
  // The rule's SINGLE COLOR data has changed.
  //-------------------------------------------------------------------------------
  function OnRuleSingleColorUpdated(updatedItem: ILayerStyleType_SingleColor)
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      single_color: updatedItem
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The rule's UNIQUE VALUES data has changed.
  //-------------------------------------------------------------------------------
  function OnRuleUniqueValuesUpdated(updatedItem: ILayerStyleType_UniqueValues)
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      unique_values: updatedItem
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The rule's CLASSIFIED data has changed.
  //-------------------------------------------------------------------------------
  function OnRuleClassifiedUpdated(updatedItem: ILayerStyleType_Classified)
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      classified: updatedItem
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The rule's GRADIENT data has changed.
  //-------------------------------------------------------------------------------
  function OnRuleGradientUpdated(updatedItem: ILayerStyleType_Gradient)
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      gradient: updatedItem
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The poly fill pattern combo box was changed.
  //-------------------------------------------------------------------------------
  const OnPolyFillPatternChanged = (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      poly_fill_pattern: event.target.value as TLayerStylePolyFillPattern
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The poly fill pattern color has changed.
  //-------------------------------------------------------------------------------
  function OnPolyFillPatternColorChanged(value: string, colors: MuiColorInputColors)
  {
    if(!props.rule.geometry) return;

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      poly_fill_pattern_color: value
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }  

  //-------------------------------------------------------------------------------
  // The poly fill pattern spacing has changed.
  //-------------------------------------------------------------------------------
  function OnPolyFillPatternSpacingChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      poly_fill_pattern_spacing: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The poly fill pattern stroke width has changed.
  //-------------------------------------------------------------------------------
  function OnPolyFillPatternStrokeWidthChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      poly_fill_pattern_stroke_width: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }

  //-------------------------------------------------------------------------------
  // The poly fill pattern opacity has changed.
  //-------------------------------------------------------------------------------
  function OnPolyFillPatternOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule.geometry) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleGeom: ILayerStyleRule_Geometry =
    {
      ...props.rule.geometry,
      poly_fill_pattern_opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleGeomUpdated(updatedRuleGeom);
  }












  

  if(!props.rule.geometry || !props.defaultRule.geometry || !props.layer || !props.layer.attributes) return null;


  // Main render

  return (

    // Static values (not tied to the color scheme)

    <Stack sx={{ mx: 0.5, my: 1 }}>

      {props.is_default_rule === false
        ?
          <Typography sx={{ mt: 0, mb: 2, color: theme_textColorMain+'70', fontSize: '0.7rem' }}>
            If a value is not set, it will be automatically inherited from the Global Defaults.  You can adjust these defaults by turning on advanced mode.
          </Typography>
        :null
      }

      {/* POINT layer only */}

      {props.layer_shape_type === 'point'
        ?
          <Stack direction='column' sx={{ mt: 0, alignItems: 'start', justifyContent: 'start' }}>

            {/* Points (UI ROW 1) */}

            <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'start' }}>

              {/* Point Type/Shape */}

              <EditLayerStyleRule_Setting_Dropdown
                   name='Point Shape' OnChangeHandler={OnPointTypeChanged}
                   value={props.rule.geometry.point_type} default_value={props.defaultRule.geometry.point_type} 
                   is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='115px'
                   drop_down_items={LAYER_STYLE_POINT_TYPES}/>

              {/* Point Size */}

              <EditLayerStyleRule_Setting_Number 
                   name='Point Size' min_value={POINT_MIN_SIZE} max_value={POINT_MAX_SIZE} OnChangeHandler={OnPointSizeChanged}
                   value={props.rule.geometry.point_size} default_value={props.defaultRule.geometry.point_size} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.8rem' width='110px'/>

              {/* Point Opacity */}

              <EditLayerStyleRule_Setting_Number 
                   name='Point Opacity' min_value={0} max_value={1} OnChangeHandler={OnPointOpacityChanged}
                   value={props.rule.geometry.point_opacity} default_value={props.defaultRule.geometry.point_opacity} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.8rem' width='140px'/>

            </Stack>

            {/* Points Halo (UI ROW 2) */}

            <Stack direction='row' sx={{ mt: 2, alignItems: 'center', justifyContent: 'start' }}>

              {/* Point Halo Size */}

              <EditLayerStyleRule_Setting_Number 
                   name='Halo Size' min_value={POINT_HALO_MIN_SIZE} max_value={POINT_HALO_MAX_SIZE} OnChangeHandler={OnPointHaloSizeChanged}
                   value={props.rule.geometry.point_halo_size} default_value={props.defaultRule.geometry.point_halo_size} 
                   is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='120px'/>

              {/* Point Halo Opacity */}

              <EditLayerStyleRule_Setting_Number 
                   name='Halo Opacity' min_value={0} max_value={1} OnChangeHandler={OnPointHaloOpacityChanged}
                   value={props.rule.geometry.point_halo_opacity} default_value={props.defaultRule.geometry.point_halo_opacity} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.8rem' width='120px'/>

              {/* Point Halo Color */}

              <EditLayerStyleRule_Setting_Color 
                   name='Halo Color' OnChangeHandler={OnPointHaloColorChanged}
                   value={props.rule.geometry.point_halo_color} default_value={props.defaultRule.geometry.point_halo_color} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.8rem' width='110px'/>

            </Stack>

          </Stack>
        :null
      }

      {/* LINE or POLYGON layers */}

      {props.layer_shape_type === 'line' || props.layer_shape_type === 'polygon'
        ?
          <Stack direction='column' sx={{ mt: 3, alignItems: 'start', justifyContent: 'start' }}>

            <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'start' }}>

              {/* Line Style */}

              <EditLayerStyleRule_Setting_Dropdown
                   name='Line Style' OnChangeHandler={OnLineStyleChanged}
                   value={props.rule.geometry.line_style} default_value={props.defaultRule.geometry.line_style} 
                   is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='120px'
                   drop_down_items={LAYER_STYLE_LINE_TYPES}/>

              {/* Line Thickness */}

              <EditLayerStyleRule_Setting_Number 
                   name='Line Thickness' min_value={LINE_THICKNESS_MIN} max_value={LINE_THICKNESS_MAX} OnChangeHandler={OnLineThicknessChanged}
                   value={props.rule.geometry.line_thickness} default_value={props.defaultRule.geometry.line_thickness} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.7rem' width='130px'/>

              {/* Line Opacity */}

              <EditLayerStyleRule_Setting_Number 
                   name='Line Opacity' min_value={0} max_value={1} OnChangeHandler={OnLineOpacityChanged}
                   value={props.rule.geometry.line_opacity} default_value={props.defaultRule.geometry.line_opacity} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.7rem' width='130px'/>

            </Stack>

            {/* Line Halo */}

            <Stack direction='row' sx={{ mt: 2, alignItems: 'center', justifyContent: 'start' }}>

              {/* Line Halo Thickness */}

              <EditLayerStyleRule_Setting_Number 
                   name='Halo Thickness' min_value={LINE_HALO_MIN_THICKNESS} max_value={LINE_HALO_MAX_THICKNESS} OnChangeHandler={OnLineHaloThicknessChanged}
                   value={props.rule.geometry.line_halo_thickness} default_value={props.defaultRule.geometry.line_halo_thickness} 
                   is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.7rem' width='130px'/>

              {/* Line Halo Opacity */}

              <EditLayerStyleRule_Setting_Number 
                   name='Halo Opacity' min_value={0} max_value={1} OnChangeHandler={OnLineHaloOpacityChanged}
                   value={props.rule.geometry.line_halo_opacity} default_value={props.defaultRule.geometry.line_halo_opacity} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.7rem' width='130px'/>

              {/* Line Halo Color */}

              <EditLayerStyleRule_Setting_Color 
                   name='Halo Color' OnChangeHandler={OnLineHaloColorChanged}
                   value={props.rule.geometry.line_halo_color} default_value={props.defaultRule.geometry.line_halo_color} 
                   is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.7rem' width='130px'/>

            </Stack>
          </Stack>
        :null
      }

      {/* POLYGON only layers */}

      {props.layer_shape_type === 'polygon'
        ?
          <Stack direction='column' sx={{ mt: 3, alignItems: 'start', justifyContent: 'start' }}>

            {/* Poly fill Opacity */}

            <EditLayerStyleRule_Setting_Number 
                  name='Poly Fill Opacity' min_value={0} max_value={1} OnChangeHandler={OnPolyFillOpacityChanged}
                  value={props.rule.geometry.poly_fill_opacity} default_value={props.defaultRule.geometry.poly_fill_opacity} 
                  is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.7rem' width='140px'/>

            <Stack direction='row' sx={{ mt: 3, alignItems: 'start', justifyContent: 'space-between' }}>

              {/* Poly fill pattern */}

              <EditLayerStyleRule_Setting_Dropdown
                   name='Poly Fill Pattern' OnChangeHandler={OnPolyFillPatternChanged}
                   value={props.rule.geometry.poly_fill_pattern} default_value={props.defaultRule.geometry.poly_fill_pattern} 
                   is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='120px'
                   drop_down_items={LAYER_STYLE_POLY_FILL_PATTERNS}/>
                    
              {/* Poly fill pattern OPTIONS */}

              {/* Poly fill pattern spacing */}

              <EditLayerStyleRule_Setting_Number 
                   name='Pattern Spacing' min_value={POLY_FILL_PATTERN_SPACING_MIN} max_value={POLY_FILL_PATTERN_SPACING_MAX} OnChangeHandler={OnPolyFillPatternSpacingChanged}
                   value={props.rule.geometry.poly_fill_pattern_spacing} default_value={props.defaultRule.geometry.poly_fill_pattern_spacing} 
                   is_default_rule={props.is_default_rule} margin_left={2} name_font_size='0.6rem' width='100px'/>

              {/* Poly fill pattern stroke width */}

              <EditLayerStyleRule_Setting_Number 
                   name='Pattern Width' min_value={POLY_FILL_PATTERN_STROKE_WIDTH_MIN} max_value={POLY_FILL_PATTERN_STROKE_WIDTH_MAX} OnChangeHandler={OnPolyFillPatternStrokeWidthChanged}
                   value={props.rule.geometry.poly_fill_pattern_stroke_width} default_value={props.defaultRule.geometry.poly_fill_pattern_stroke_width} 
                   is_default_rule={props.is_default_rule} margin_left={2} name_font_size='0.6rem' width='90px'/>

              {/* Poly fill pattern color */}

              <EditLayerStyleRule_Setting_Color 
                   name='Pattern Color' OnChangeHandler={OnPolyFillPatternColorChanged}
                   value={props.rule.geometry.poly_fill_pattern_color} default_value={props.defaultRule.geometry.poly_fill_pattern_color} 
                   is_default_rule={props.is_default_rule} margin_left={2} name_font_size='0.6rem' width='110px'/>

              {/* Poly fill pattern opacity */}

              <EditLayerStyleRule_Setting_Number 
                   name='Pattern Opacity' min_value={0} max_value={1} OnChangeHandler={OnPolyFillPatternOpacityChanged}
                   value={props.rule.geometry.poly_fill_pattern_opacity} default_value={props.defaultRule.geometry.poly_fill_pattern_opacity} 
                   is_default_rule={props.is_default_rule} margin_left={2} name_font_size='0.6rem' width='90px'/>

            </Stack>
 
          </Stack>
        :null
      }

      {/* Color scheme & attribute selection */}

      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mt: 3 }}>

        {/* Color scheme selection */}

        <Stack direction='column' sx={{ ml: 0, mt: 0 }}>

          <CustomSelect variant='standard' 
                        value={props.rule.geometry.color_scheme}
                        onChange={OnColorSchemeTypeChanged}
                        sx={{ p: 0 }}>

            <MenuItem key='none' value='none'>

              {props.is_default_rule 
                ? //This is inside the special "Default Rule" - show "Not Set"
                    <Stack direction='row' sx={{ alignItems: 'center' }}>
                      <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
                        Color Scheme:
                      </Typography>
                      <Typography sx={{ ml: 1, color: theme_textColorMain+'70', fontSize: '1.2rem' }}>
                        Not Set
                      </Typography>
                    </Stack>
                : //This is a regular rule - show "Default"
                  <Stack>
                    <Stack direction='row' sx={{ alignItems: 'center' }}>
                      <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
                        Color Scheme:
                      </Typography>
                      <Typography sx={{ ml: 1, color: theme_textColorMain+'70', fontSize: '1.2rem' }}>
                        Default
                      </Typography>
                    </Stack>
                    <Typography sx={{ color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                      Use the default color scheme
                    </Typography>
                  </Stack>
              }

            </MenuItem>

            <MenuItem key='single color' value='single color'>
              <Stack>
                <Stack direction='row' sx={{ alignItems: 'center' }}>
                  <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
                    Color Scheme:
                  </Typography>
                  <Typography sx={{ ml: 1, color: theme_orange, fontSize: '1.2rem' }}>
                    Single Color
                  </Typography>
                </Stack>
                <Typography sx={{ color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                  Use a single color for all objects
                </Typography>
              </Stack>
            </MenuItem>

            <MenuItem key='unique values' value='unique values'>
              <Stack>
                <Stack direction='row' sx={{ alignItems: 'center' }}>
                  <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
                    Color Scheme:
                  </Typography>
                  <Typography sx={{ ml: 1, color: theme_orange, fontSize: '1.2rem' }}>
                    Unique Values
                  </Typography>
                </Stack>
                <Typography sx={{ color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                  Assign colors for all unique values of an attribute
                </Typography>
              </Stack>
            </MenuItem>

            <MenuItem key='classified' value='classified'>
              <Stack>
                <Stack direction='row' sx={{ alignItems: 'center' }}>
                  <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
                    Color Scheme:
                  </Typography>
                  <Typography sx={{ ml: 1, color: theme_orange, fontSize: '1.2rem' }}>
                    Classified
                  </Typography>
                </Stack>
                <Typography sx={{ color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                  Set up colors for numerical ranges of an attribute's value
                </Typography>
              </Stack>
            </MenuItem>

            <MenuItem key='gradient' value='gradient'>
              <Stack>
                <Stack direction='row' sx={{ alignItems: 'center' }}>
                  <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
                    Color Scheme:
                  </Typography>
                  <Typography sx={{ ml: 1, color: theme_orange, fontSize: '1.2rem' }}>
                    Gradient
                  </Typography>
                </Stack>
                <Typography sx={{ color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                  Set up a color gradient based on a numerical attribute's value
                </Typography>
              </Stack>
            </MenuItem>

          </CustomSelect>

          {RenderDefaultValue_Dropdown(props.rule.geometry.color_scheme, props.defaultRule.geometry.color_scheme, props.is_default_rule, 'none')}

        </Stack>

        {/* Attribute selection - Shown for all types except 'None' and 'Single Color' */}

        {props.rule.geometry.color_scheme !== 'none' && props.rule.geometry.color_scheme !== 'single color'
          ?
            <Stack direction='row' sx={{ width: '100%', ml: 2, justifyContent: 'space-between' }}>

              {/* Combo box label */}

              <Stack direction='column' sx={{ width: '100%' }}>

                <Stack direction='row'>
                  <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                    Attribute
                  </Typography>

                  {props.rule.geometry.attribute_id
                    ?
                      <Typography sx={{ ml: 1, color: theme_textColorMain+'A0', fontSize: '0.8rem' }}>
                        ({props.rule.geometry.attribute_type})
                      </Typography>
                    :null
                  }
                </Stack>

                {/* Combo box */}

                <CustomSelect variant='standard' size='small'
                              value={props.rule.geometry.attribute_id ?? ''}
                              onChange={OnAttributeChanged}
                              sx={{ p: 0.5 }}>

                  {props.layer.attributes.map(function(attribute)
                  {
                    // Don't show the geometry attribute
                    if(attribute.attribute_name === 'geom' || attribute.attribute_name === 'geometry')
                      return null;

                    // For classified and gradient color schemes, only list numerical attributes
                    if(attribute.data_type !== 'number' && (props.rule.geometry?.color_scheme === 'classified' || props.rule.geometry?.color_scheme === 'gradient')) return null

                    return (
                      <MenuItem key={attribute.attribute_id} value={attribute.attribute_id}>
                        <Stack>
                          <Stack direction='row' sx={{ alignItems: 'center' }}>
                            <Typography sx={{ color: theme_orange+'D0', fontSize: '1.0rem' }}>
                              {attribute.attribute_name}
                            </Typography>
                            <Typography sx={{ ml: 1, color: theme_limeGreen, opacity: 0.7, fontSize: '0.8rem' }}>
                              {attribute.unit}
                            </Typography>
                          </Stack>
                          <TypographyWithAutoTooltip sx={{ maxWidth: '300px', color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                            {attribute.description}
                          </TypographyWithAutoTooltip>
                        </Stack>
                      </MenuItem>
                    )
                  })}

                </CustomSelect>
              </Stack>

            </Stack>
          :null
        }

      </Stack>

      {/* Notify user if an attribute is not selected */}

      {props.rule.geometry.attribute_id === undefined && props.rule.geometry.color_scheme !== 'none' && props.rule.geometry.color_scheme !== 'single color'
        ?
          <Typography sx={{ mt: 1, fontSize: '1.0rem', color: theme_errorRed, opacity: 0.7, width: '100%', textAlign: 'right' }}>
            Please select an attribute.
          </Typography>
        :null
      }

      {/* Color schemes (only one can be active at a time) */}

      <EditLayerStyle_ColorScheme_SingleColor ruleGeom={props.rule.geometry} OnRuleSingleValueUpdated={OnRuleSingleColorUpdated}/>
      <EditLayerStyle_ColorScheme_UniqueValues rule={props.rule} OnRuleUniqueValuesUpdated={OnRuleUniqueValuesUpdated} OnExpandRule={props.OnExpandRule}/>
      <EditLayerStyle_ColorScheme_Classified rule={props.rule} OnRuleClassifiedUpdated={OnRuleClassifiedUpdated} layer={props.layer} OnExpandRule={props.OnExpandRule}/>
      <EditLayerStyle_ColorScheme_Gradient ruleGeom={props.rule.geometry} OnRuleGradientUpdated={OnRuleGradientUpdated}/>

    </Stack>
  )
}





//-------------------------------------------------------------------------------
// Get the min/max values of a numerical attribute.
//-------------------------------------------------------------------------------
export async function GetAttribMinMax(attribute_id: number | undefined): Promise<boolean>
{
  if(!attribute_id)
  {
    Debug.error('GetAttribMinMax> ERROR: undefined or invalid attribute ID');
    return false;
  }

  // Call the server

  const server = new CallServer();
  server.Add("attribute_id", attribute_id);
  server.Add("mode", "min_max");

  //useStore.getState().store_setDeletingLayerLibraryGroup(true);  // Tell the UI we are doing work

  const result = await server.Call('get', '/attribute_info');

  //useStore.getState().store_setDeletingLayerLibraryGroup(false); // Tell the UI we are no longer doing work

  if(result.success)
  {
    Debug.log('GetAttribMinMax> API server call SUCCESS');
    //Debug.log('GetAttribMinMax> SUCCESS! data=' + JSON.stringify(result.data));
    
    // Delete the group from the state store
    //useStore.getState().store_removeLayerLibraryGroup(groupID);

    //Debug.log(`GetAttribMinMax> min=${min}  max=${max}.`);
    return true;  // success
  }
  else
  {
    // Failure
    ToastNotification('error', "Unable to get attribute value min/max")
    Debug.error(`GetAttribMinMax> ${result.errorCode} - ${result.errorMessage}`);
    return false;
  }
}

//-------------------------------------------------------------------------------
// 
//-------------------------------------------------------------------------------
function GetLayerStyleAttribType(layer: ILayer, attribute_id: number): TLayerStyleAttribType | undefined
{
  const attribInfo: IVectorLayerAttribute | undefined = GetVectorLayerAttributeByID(layer, attribute_id);
  if(!attribInfo)
    return undefined;

  if(attribInfo.data_type === 'number') 
    return 'number';

  if(attribInfo.data_type === 'string' || attribInfo.data_type === 'enum') 
    return 'string';

  return undefined;
}

//-------------------------------------------------------------------------------
// Validates the specified rule geometry section.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//-------------------------------------------------------------------------------
export function ValidateLayerStyleRuleGeom(layer_shape_type: TLayerStyleShapeType, 
                                           ruleGeom: ILayerStyleRule_Geometry | undefined, 
                                           default_ruleGeom: ILayerStyleRule_Geometry,
                                           is_default_rule: boolean,
                                           errMsgPrefix: string): string
{
  if(!ruleGeom) return '';  // It's ok for a rule to not have a geometry section

  let errStr: string = '';

  // Validate only the settings that are appropriate for the layer's geometry type (point/line/polygon)

  if(layer_shape_type === 'point')
  {
    // Validate POINT settings

    // Point shape/type
    errStr = ValidateLayerStyle_Enum('Point Shape',ruleGeom.point_type,default_ruleGeom.point_type,is_default_rule,LAYER_STYLE_POINT_TYPES,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Point size
    errStr = ValidateLayerStyle_Number('Point Size',ruleGeom.point_size,default_ruleGeom.point_size,is_default_rule,POINT_MIN_SIZE,POINT_MAX_SIZE,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Point opacity
    errStr = ValidateLayerStyle_Number('Point Opacity',ruleGeom.point_opacity,default_ruleGeom.point_opacity,is_default_rule,0,1,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Halo size/thickness
    errStr = ValidateLayerStyle_Number('Point Halo Size',ruleGeom.point_halo_size,default_ruleGeom.point_halo_size,is_default_rule,POINT_HALO_MIN_SIZE,POINT_HALO_MAX_SIZE,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Halo opacity
    errStr = ValidateLayerStyle_Number('Point Halo Opacity',ruleGeom.point_halo_opacity,default_ruleGeom.point_halo_opacity,is_default_rule,0,1,errMsgPrefix);
    if(errStr!=='') return errStr;
  }
  else
  {
    // Validate LINE or POLYGON settings

    // Line style
    let errStr: string = ValidateLayerStyle_Enum('Line Style',ruleGeom.line_style,default_ruleGeom.line_style,is_default_rule,LAYER_STYLE_LINE_TYPES,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Line thickness
    errStr = ValidateLayerStyle_Number('Line Thickness',ruleGeom.line_thickness,default_ruleGeom.line_thickness,is_default_rule,LINE_THICKNESS_MIN,LINE_THICKNESS_MAX,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Line opacity
    errStr = ValidateLayerStyle_Number('Line Opacity',ruleGeom.line_opacity,default_ruleGeom.line_opacity,is_default_rule,0,1,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Line halo thickness
    errStr = ValidateLayerStyle_Number('Line Halo Thickness',ruleGeom.line_halo_thickness,default_ruleGeom.line_halo_thickness,is_default_rule,LINE_HALO_MIN_THICKNESS,LINE_HALO_MAX_THICKNESS,errMsgPrefix);
    if(errStr!=='') return errStr;

    // Line halo opacity
    errStr = ValidateLayerStyle_Number('Line Halo Opacity',ruleGeom.line_halo_opacity,default_ruleGeom.line_halo_opacity,is_default_rule,0,1,errMsgPrefix);
    if(errStr!=='') return errStr;

    if(layer_shape_type === 'line')
    {
      // Validate LINE-ONLY settings

      // (At this time there are no line-only settings)
    }
    else if(layer_shape_type === 'polygon')
    {
      // Validate POLYGON-ONLY settings

      // Poly fill opacity
      errStr = ValidateLayerStyle_Number('Poly Fill Opacity',ruleGeom.poly_fill_opacity,default_ruleGeom.poly_fill_opacity,is_default_rule,0,1,errMsgPrefix);
      if(errStr!=='') return errStr;

      // Poly Fill Pattern
      errStr = ValidateLayerStyle_Enum('Poly Fill Pattern',ruleGeom.poly_fill_pattern,default_ruleGeom.poly_fill_pattern,is_default_rule,LAYER_STYLE_POLY_FILL_PATTERNS,errMsgPrefix);
      if(errStr!=='') return errStr;

      // Poly Fill Pattern Spacing
      errStr = ValidateLayerStyle_Number('Poly Fill Pattern Spacing',ruleGeom.poly_fill_pattern_spacing,default_ruleGeom.poly_fill_pattern_spacing,is_default_rule,POLY_FILL_PATTERN_SPACING_MIN,POLY_FILL_PATTERN_SPACING_MAX,errMsgPrefix);
      if(errStr!=='') return errStr;      

      // Poly Fill Pattern Width
      errStr = ValidateLayerStyle_Number('Poly Fill Pattern Width',ruleGeom.poly_fill_pattern_stroke_width,default_ruleGeom.poly_fill_pattern_stroke_width,is_default_rule,POLY_FILL_PATTERN_STROKE_WIDTH_MIN,POLY_FILL_PATTERN_STROKE_WIDTH_MAX,errMsgPrefix);
      if(errStr!=='') return errStr;      

      // Poly Fill Pattern Opacity
      errStr = ValidateLayerStyle_Number('Poly Fill Pattern Opacity',ruleGeom.poly_fill_pattern_opacity,default_ruleGeom.poly_fill_pattern_opacity,is_default_rule,0,1,errMsgPrefix);
      if(errStr!=='') return errStr;
    }
  }

  // Validate the attribute (applicable to all color schemes EXCEPT Single Color)

  if((ruleGeom.color_scheme === 'unique values' || ruleGeom.color_scheme === 'classified' || ruleGeom.color_scheme === 'gradient') &&
     (ruleGeom.attribute_id === undefined || ruleGeom.attribute_type === undefined))
    return errMsgPrefix + 'No attribute selected';

  // Validate the currently-selected color scheme

  switch(ruleGeom.color_scheme)
  {
    case 'single color': 
      errStr = ValidateLayerStyleRuleGeom_SingleColor(ruleGeom.single_color, errMsgPrefix + ' Single Color: '); 
      if(errStr!=='') return errStr;
      break;
    case 'unique values': 
      errStr = ValidateLayerStyleRuleGeom_UniqueValues(ruleGeom.unique_values, errMsgPrefix + ' Unique Values: '); 
      if(errStr!=='') return errStr;
      break;
    case 'classified': 
      errStr = ValidateLayerStyleRuleGeom_Classified(ruleGeom.classified, errMsgPrefix + ' Classified: '); 
      if(errStr!=='') return errStr;
      break;
    case 'gradient': 
      errStr = ValidateLayerStyleRuleGeom_Gradient(ruleGeom.gradient, errMsgPrefix + ' Gradient: '); 
      if(errStr!=='') return errStr;
      break;

    case 'none': 
      if(is_default_rule === false && ruleGeom.color_scheme.toLocaleLowerCase() === 'none' && 
        (default_ruleGeom.color_scheme === undefined || default_ruleGeom.color_scheme.toLocaleLowerCase() === 'none' || LAYER_STYLE_RULE_COLOR_SCHEMES.includes(default_ruleGeom.color_scheme) === false))
        return `${errMsgPrefix} Color scheme is not set`;
      break;

    default: 
      return errMsgPrefix + 'Invalid or unsupported color scheme';
  }

  // Success
  return '';
}

//-------------------------------------------------------------------------------
// Validates the specified NUMBER setting.
// 
// If a value is UNDEFINED in a regular rule, it assumes inheritance and also 
// checks the default rule value.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//
// 
//-------------------------------------------------------------------------------
export function ValidateLayerStyle_Number(name: string, 
                                          value: number | undefined, 
                                          default_value: number | undefined, 
                                          is_default_rule: boolean,
                                          min_value: number,
                                          max_value: number,
                                          errMsgPrefix: string): string
{
  if(value !== undefined && (value < min_value || value > max_value))
    return `${errMsgPrefix} ${name} is out of bounds`;

  if(is_default_rule === false && value === undefined && default_value === undefined)
    return `${errMsgPrefix} ${name} is not set`;

  // Success
  return '';
}

//-------------------------------------------------------------------------------
// Validates the specified ENUM/STRING setting.
// 
// If a value is UNDEFINED in a regular rule, it assumes inheritance and also 
// checks the default rule value.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//
// 
//-------------------------------------------------------------------------------
export function ValidateLayerStyle_Enum(name: string, 
                                        value: string | undefined, 
                                        default_value: string | undefined, 
                                        is_default_rule: boolean,
                                        list_of_all_values: readonly string[],
                                        errMsgPrefix: string): string
{
  if(value === undefined || list_of_all_values.includes(value) === false)
    return `${errMsgPrefix} ${name} is invalid`;
  
  if(is_default_rule === false && value.toLocaleLowerCase() === 'none' && 
     (default_value === undefined || default_value.toLocaleLowerCase() === 'none' || list_of_all_values.includes(default_value) === false))
    return `${errMsgPrefix} ${name} is not set`;

  // Success
  return '';
}